import {ApiResponse} from '@legit.health/backend-client';
import {ONE_HOUR_IN_MS, generatePath, DiagnosticReportId} from '@legit.health/ui';
import {QueryClient} from '@tanstack/react-query';
import {
  DiagnosticReport,
  DiagnosticReportApiResponse
} from '@/practitioner/interfaces/DiagnosticReport';
import {DIAGNOSTIC_REPORT} from '@/shared/const/backend';
import useSuspenseApiQuery from '@/shared/hooks/useSuspenseApiQuery';
import {
  DiagnosticReportOutdatedEvent,
  DIAGNOSTIC_REPORT_OUTDATED
} from './diagnosticReportOutdatedEvent';
import {
  DIAGNOSTIC_REPORT_PATCHED_EVENT,
  DiagnosticReportPatchedEvent
} from './diagnosticReportPatchedEvent';
import {ENCOUNTER_IS_BASAL_CHANGED} from './isBasalChanged';

function onOutdated(queryClient: QueryClient, event: DiagnosticReportOutdatedEvent) {
  queryClient.invalidateQueries({queryKey: getQueryKey(event.diagnosticReportId)});
}

function onPatched(queryClient: QueryClient, event: DiagnosticReportPatchedEvent) {
  queryClient.setQueryData<ApiResponse<DiagnosticReport> | null>(
    getQueryKey(event.diagnosticReport.id),
    {
      isSuccessfull: true,
      status: 200,
      data: event.diagnosticReport
    }
  );
}

function onIsBasalChanged(queryClient: QueryClient) {
  queryClient.invalidateQueries({queryKey: getBaseQueryKey()});
}

export function getBaseQueryKey() {
  return [DIAGNOSTIC_REPORT];
}

export function getQueryKey(diagnosticReportId: DiagnosticReportId) {
  return [DIAGNOSTIC_REPORT, diagnosticReportId];
}

export default function useGetDiagnosticReport(diagnosticReportId: DiagnosticReportId) {
  return useSuspenseApiQuery<DiagnosticReportApiResponse>({
    queryOptions: {
      queryKey: getQueryKey(diagnosticReportId),
      staleTime: ONE_HOUR_IN_MS
    },
    requestArguments: {
      method: 'GET',
      path: generatePath(DIAGNOSTIC_REPORT, {diagnosticReportId})
    },
    subscribeToEventsOptions: {
      dataKey: DIAGNOSTIC_REPORT,
      subscriptions: [
        [DIAGNOSTIC_REPORT_OUTDATED, onOutdated],
        [DIAGNOSTIC_REPORT_PATCHED_EVENT, onPatched],
        [ENCOUNTER_IS_BASAL_CHANGED, onIsBasalChanged]
      ]
    }
  });
}
